// Pip Bennett — FAQ content
// Future FAQs: add new {q, a} pairs to any category, or add a new category
// object to CATEGORIES. The page + JSON-LD update automatically.

export const CATEGORIES = [
  {
    id: "working-with-pip",
    label: "Working With Pip",
    eyebrow: "Where to begin",
    items: [
      {
        q: "What kinds of people do you work with?",
        a: `I work with people navigating life transitions — some chosen, and some that arrive unexpectedly. Many of my clients are moving through periods of emotional overwhelm, menopause, identity shifts, burnout, anxiety, confidence loss, relationship changes or a growing sense that the way they've been living no longer feels aligned.

Some people come to me seeking clarity. Others want support rebuilding confidence, emotional steadiness, self-trust or meaningful personal change. My work is deeply individual and tailored to the person sitting in front of me.`,
      },
      {
        q: "Do I need to be experiencing menopause to work with you?",
        a: `Not at all. While menopause and midlife transitions are a significant part of my work, I also support people, including men navigating stress, emotional exhaustion, confidence issues, life direction changes, burnout, grief, identity shifts and periods of personal transformation.

At the heart of my work is helping people reconnect with themselves and move through change with greater clarity, confidence and emotional wellbeing.`,
      },
      {
        q: "What is it like to work with you?",
        a: `Clients often describe our sessions as insightful, grounding and deeply supportive. My approach blends practical support with emotional depth, helping you understand not only what's happening in your life, but why certain patterns may feel difficult to shift.

I bring together coaching, clinical hypnotherapy, neuroscience-informed approaches and reflective conversation in a way that feels collaborative, personalised and genuinely transformative.`,
      },
      {
        q: "Do you work with clients internationally?",
        a: `Yes. All sessions are currently offered online via Zoom, and I work with clients across Australia and internationally.

Many clients appreciate being able to access support from the comfort and privacy of their own environment.`,
      },
    ],
  },
  {
    id: "coaching-hypnotherapy",
    label: "Coaching & Clinical Hypnotherapy",
    eyebrow: "Method",
    items: [
      {
        q: "What is Clinical Hypnotherapy?",
        a: `Clinical Hypnotherapy is an evidence based approach that works with the subconscious mind to help shift unhelpful patterns, beliefs and emotional responses. It can support areas such as anxiety, confidence, stress, emotional overwhelm, sleep, habits, self-worth and life transitions.

Contrary to popular myths, hypnotherapy is not about losing control or being 'put under'. It's a collaborative process designed to help create meaningful and lasting change.`,
      },
      {
        q: "What does hypnotherapy feel like?",
        a: `Most people describe hypnotherapy as feeling deeply relaxed, focused and aware. You remain conscious and in control throughout the session.

It's often compared to the feeling of becoming absorbed in a book, a movie or a daydream — calm, present and highly focused.`,
      },
      {
        q: "What's the difference between coaching and therapy?",
        a: `Coaching is generally future focused and designed to help people create meaningful change, build self awareness, strengthen confidence, navigate transitions and move forward with greater clarity and intention.

Therapy often focuses more heavily on diagnosing and treating mental health conditions, processing trauma, or working with significant psychological distress.

My work sits within coaching and clinical hypnotherapy, offering emotionally intelligent, practical support for people navigating change, identity shifts, emotional overwhelm, burnout, menopause and periods of personal transformation.

While many clients find this work deeply therapeutic, it is not therapy or a replacement for psychological or psychiatric care where that level of support is needed.`,
      },
      {
        q: "Do I need to do Clinical Hypnotherapy to work with you?",
        a: `Not at all. Clinical Hypnotherapy is available as part of my work, but it is never something clients are pressured into or required to do.

Some clients love working at the subconscious level through hypnotherapy, while others prefer a more coaching based approach using strategic conversation, neuroscience-informed tools, mindset work and behavioural change techniques.

Both approaches can create profound and meaningful transformation. Our work together is always tailored to the individual, their comfort level and the kind of support that feels most aligned for them.`,
      },
      {
        q: "Can online hypnotherapy really work?",
        a: `Yes. Online hypnotherapy has been shown to be highly effective for many people and offers the added benefit of being able to access support from a familiar environment.

Many clients find they feel more relaxed and comfortable participating in sessions from home.`,
      },
      {
        q: "How can I work with you?",
        a: `I currently offer two private support pathways: a 3-month package, and a deeper 6-month transformational package. Some clients choose the 3-month pathway for focused support during a specific transition, while others feel called toward the deeper integration and transformation that comes through longer-term work over 6 months.

This work is designed to create meaningful and lasting change, which is why I no longer offer casual one-off sessions. Sustainable transformation takes time, support and space to integrate.

Together, we create a process that supports meaningful, sustainable change rather than quick fixes or short-term motivation.

I also periodically run live online groups and workshops. The best way to hear about upcoming offerings is by joining my newsletter community.`,
      },
    ],
  },
  {
    id: "menopause-midlife",
    label: "Menopause & Midlife",
    eyebrow: "This season",
    items: [
      {
        q: "Why can menopause feel so emotionally overwhelming?",
        a: `Menopause is not simply a hormonal transition — it can also affect identity, confidence, relationships, sleep, emotional regulation and the nervous system.

Many women reach this stage of life carrying years of pressure, responsibility and emotional load. As hormones shift, it's common for previously suppressed stress, needs or emotions to become more noticeable.

This does not mean you're falling apart. Often, it means something important is asking for attention and change.`,
      },
      {
        q: "Can hypnotherapy help with menopause symptoms?",
        a: `Clinical Hypnotherapy may support many of the emotional and behavioural aspects of menopause including stress, mild anxiety, confidence, sleep difficulties, emotional overwhelm and nervous system dysregulation.

It is not a replacement for medical care, but many women find it to be a powerful complementary support alongside medical and lifestyle approaches.`,
      },
      {
        q: "Do you work with men in midlife too?",
        a: `Yes. I work with both women and men navigating periods of transition, reinvention, emotional exhaustion, identity shifts and questions around meaning, direction and fulfilment.

Midlife can be a deeply transformative period for many people, and support during that time can make a significant difference.`,
      },
      {
        q: "Why do life transitions affect us so deeply?",
        a: `Periods of change often challenge our sense of certainty, identity and control. Even positive transitions can create emotional disorientation because the brain and nervous system are adjusting to something unfamiliar.

Support during these periods can help create greater steadiness, perspective and confidence while navigating change.`,
      },
    ],
  },
  {
    id: "practical",
    label: "Practical Questions",
    eyebrow: "Logistics",
    items: [
      {
        q: "How do I book a session?",
        a: `Sessions can be booked directly through the website using the booking links provided throughout the site.`,
      },
      {
        q: "Do you offer one-off sessions?",
        a: `I no longer offer standalone one-off sessions. Over the years, I've found that deeper and more lasting transformation happens through ongoing support and consistent work together over time.

My current private offerings are structured as 3-month and 6-month coaching and hypnotherapy experiences designed to support real, integrated change.

I also offer live online groups and workshops throughout the year.`,
      },
      {
        q: "Do I need to prepare anything before a session?",
        a: `No formal preparation is required. Most people simply arrive exactly as they are.

Our work begins with understanding where you are now, what's feeling difficult, and what kind of change or support you're seeking.`,
      },
      {
        q: "What time zones do you work across?",
        a: `Because sessions are online, I work with clients across Australia and internationally. Session times are arranged individually based on availability and time zone compatibility.`,
      },
    ],
  },
  {
    id: "ethical-scope",
    label: "Ethical Scope & Client Suitability",
    eyebrow: "Boundaries of care",
    items: [
      {
        q: "Who is this work not suitable for?",
        a: `My work is designed to support emotionally stable adults navigating life transitions, emotional wellbeing, confidence, mindset, identity shifts, menopause, burnout, stress and personal growth.

As an ICF credentialed coach and Clinical Hypnotherapist, I work within clear professional and ethical boundaries. I do not diagnose or treat psychiatric or medical conditions, and my services are not a replacement for psychological, psychiatric or medical care.

This work is not suitable for individuals experiencing severe or unmanaged mental health conditions including psychosis, schizophrenia, bipolar disorder, severe clinical depression, active suicidality or other psychiatric conditions requiring specialist medical or psychiatric treatment.

Where appropriate, I may recommend working alongside or being referred to an appropriately qualified medical or mental health professional.

Client safety, ethical practice and appropriate support are always prioritised within my work.`,
      },
    ],
  },
];

// Flatten for FAQPage JSON-LD generation
export function getFaqJsonLd() {
  const mainEntity = [];
  for (const cat of CATEGORIES) {
    for (const item of cat.items) {
      mainEntity.push({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a.replace(/\n+/g, " ").trim(),
        },
      });
    }
  }
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}
